import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router";
import { Box, Typography, IconButton, Skeleton } from "@mui/material";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import { fetchProgressPhotos, resetProgressPhotos } from "../../../redux/slices/progressPhotosSlice";

interface SiteProgressCarouselProps {
  /** Fall back to these URLs when no Dropbox photos are available. */
  fallbackImages?: string[];
  /** Scope photos to this project ID. Re-fetches when the ID changes. */
  projectId?: string;
}

/**
 * SiteProgressCarousel — fetches the most recently modified site-progress
 * photos from Dropbox via the backend cache and renders them in an
 * interactive carousel with the same visual style as the former inline
 * SitePhotosCarousel. Results are cached server-side for 3.5 hours so
 * subsequent loads are near-instant.
 */
const SiteProgressCarousel = ({ fallbackImages = [], projectId }: SiteProgressCarouselProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { photos, status, currentProjectId } = useAppSelector((state) => state.progressPhotos);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (projectId === undefined) return; // wait until we know which project
    // Already have data for this project or a fetch is in flight — do nothing.
    if (currentProjectId === projectId && (status === "loading" || status === "succeeded")) return;
    // Project switched — reset first, then fetch.
    if (currentProjectId !== projectId) {
      dispatch(resetProgressPhotos());
    }
    dispatch(fetchProgressPhotos(projectId));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, projectId]); // intentionally omit status/currentProjectId — they must not re-trigger

  // Build the final image list: real Dropbox links when available, fallback otherwise.
  const images: string[] = (() => {
    if (status === "succeeded" && photos.length > 0) {
      // Prefer thumbnail_url (w1024h768 data URI) for fast carousel display;
      // fall back to temporary_link (full-res) if thumbnail wasn't generated.
      return photos.map((p) => p.thumbnail_url || p.temporary_link);
    }
    if (status === "succeeded" && fallbackImages.length > 0) {
      return fallbackImages;
    }
    if (status === "failed" && fallbackImages.length > 0) {
      return fallbackImages;
    }
    return fallbackImages;
  })();

  const safeIndex = images.length > 0 ? currentIndex % images.length : 0;

  const handlePrev = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
    },
    [images.length]
  );

  const handleNext = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      setCurrentIndex((prev) => (prev + 1) % images.length);
    },
    [images.length]
  );

  const isLoading = status === "loading" || status === "idle";
  const isUsingFallback = !(status === "succeeded" && photos.length > 0);

  // Show nothing if loading finished and there are truly no images at all
  if (!isLoading && images.length === 0) return null;

  return (
    <Box>
      <Typography
        sx={{
          fontFamily: '"Brandon Grotesque", sans-serif',
          fontWeight: 700,
          fontSize: "0.85rem",
          letterSpacing: "0.15em",
          textTransform: "uppercase",
          color: "var(--on-surface)",
          mb: 3,
        }}
      >
        Recent Site Progress
      </Typography>

      {isLoading ? (
        <Box sx={{ position: "relative", width: "100%", height: { xs: 260, md: 420 } }}>
          <Skeleton
            variant="rectangular"
            sx={{
              width: "100%",
              height: "100%",
              borderRadius: 0,
            }}
          />
          {/* Shimmer label centred over the skeleton */}
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              pointerEvents: "none",
            }}
          >
            <Typography
              sx={{
                fontFamily: '"Brandon Grotesque", sans-serif',
                fontSize: "0.6rem",
                letterSpacing: "0.3em",
                textTransform: "uppercase",
                color: "transparent",
                backgroundImage:
                  "linear-gradient(90deg, #969696 0%, #dcdcdc 40%, #969696 60%, #969696 100%)",
                backgroundSize: "200% 100%",
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                animation: "shimmerText 2s linear infinite",
                "@keyframes shimmerText": {
                  "0%": { backgroundPosition: "100% 0" },
                  "100%": { backgroundPosition: "-100% 0" },
                },
              }}
            >
              Checking source files…
            </Typography>
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            borderRadius: 0,
            overflow: "hidden",
            position: "relative",
            width: "100%",
            height: { xs: 260, md: 420 },
            backgroundColor: "var(--surface-container, #efefef)",
            cursor: "pointer",
          }}
          onClick={() => navigate("/project-home/documents")}
        >
          {/* All images stacked; only the active one is visible */}
          {images.map((img, i) => (
            <Box
              key={img}
              component="img"
              src={img}
              alt={
                status === "succeeded" && photos[i]
                  ? photos[i].name
                  : `Site progress ${i + 1}`
              }
              loading="lazy"
              sx={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                objectFit: "cover",
                objectPosition: "center",
                opacity: i === safeIndex ? 1 : 0,
                transition: "opacity 0.5s ease",
              }}
            />
          ))}

          {/* Bottom gradient overlay */}
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(to top, rgba(45,52,53,0.55) 0%, rgba(45,52,53,0.1) 40%, transparent 60%)",
              pointerEvents: "none",
            }}
          />

          {/* Bottom bar: coming-soon label + nav arrows */}
          <Box
            sx={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              px: { xs: 2.5, md: 3.5 },
              pb: 2.5,
              pt: 5,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-end",
            }}
          >
            {isUsingFallback && (
              <Typography
                sx={{
                  fontFamily: '"Brandon Grotesque", sans-serif',
                  fontSize: "0.65rem",
                  letterSpacing: "0.15em",
                  textTransform: "uppercase",
                  color: "rgba(255,255,255,0.85)",
                }}
              >
                Site progress photos are coming soon
              </Typography>
            )}
            {images.length > 1 && (
              <Box sx={{ display: "flex", gap: 0.5, flexShrink: 0 }}>
                <IconButton
                  onClick={handlePrev}
                  size="small"
                  sx={{
                    color: "#ffffff",
                    backgroundColor: "rgba(255,255,255,0.15)",
                    backdropFilter: "blur(8px)",
                    width: 32,
                    height: 32,
                    "&:hover": { backgroundColor: "rgba(255,255,255,0.3)" },
                  }}
                >
                  <NavigateBeforeIcon fontSize="small" />
                </IconButton>
                <IconButton
                  onClick={handleNext}
                  size="small"
                  sx={{
                    color: "#ffffff",
                    backgroundColor: "rgba(255,255,255,0.15)",
                    backdropFilter: "blur(8px)",
                    width: 32,
                    height: 32,
                    "&:hover": { backgroundColor: "rgba(255,255,255,0.3)" },
                  }}
                >
                  <NavigateNextIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
          </Box>

          {/* Photo counter pill */}
          {images.length > 1 && (
            <Box
              sx={{
                position: "absolute",
                top: 12,
                right: 14,
                backgroundColor: "rgba(0,0,0,0.35)",
                backdropFilter: "blur(8px)",
                borderRadius: 0,
                px: 1.2,
                py: 0.3,
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.6rem",
                  color: "rgba(255,255,255,0.85)",
                  fontFamily: '"Brandon Grotesque", sans-serif',
                  letterSpacing: "0.05em",
                }}
              >
                {safeIndex + 1} / {images.length}
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default SiteProgressCarousel;
